
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { getUserTeamIds } from "@/pages/guards/services/guardFetchService";
import { useSupabaseUser } from "@/hooks/use-supabase-user";
import { useEffect, useState } from "react";

interface Team {
  id: string;
  name: string;
  description?: string;
}

interface TeamAssignmentProps {
  clientId: string;
  onComplete?: () => void;
  displayOnly?: boolean;
}

export const TeamAssignment = ({ clientId, onComplete, displayOnly = false }: TeamAssignmentProps) => {
  const [teams, setTeams] = useState<Team[]>([]);
  const [selectedTeams, setSelectedTeams] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const { toast } = useToast();
  const { user } = useSupabaseUser();

  // Fetch all teams and the ones assigned to this client
  useEffect(() => {
    const fetchTeams = async () => {
      setIsLoading(true);
      try {
        // Fetch all teams
        let { data: teamsData, error: teamsError } = await supabase
          .from('teams')
          .select('*')
          .order('name');

        if (teamsError) throw teamsError;
        
        // Fetch teams assigned to this client
        const { data: clientTeams, error: clientTeamsError } = await supabase
          .from('client_teams')
          .select('team_id')
          .eq('client_id', clientId);

        if (clientTeamsError) throw clientTeamsError;

        // Set selected teams
        const assignedTeamIds = clientTeams?.map(ct => ct.team_id) || [];
        setSelectedTeams(assignedTeamIds);
        
        // Set all teams
        setTeams(teamsData || []);
      } catch (error: any) {
        console.error('Error fetching teams:', error);
        toast({
          title: 'Error',
          description: 'Failed to load teams',
          variant: 'destructive'
        });
      } finally {
        setIsLoading(false);
      }
    };

    if (clientId) {
      fetchTeams();
    }
  }, [clientId, toast]);

  const handleTeamToggle = (teamId: string) => {
    setSelectedTeams(prev => {
      if (prev.includes(teamId)) {
        return prev.filter(id => id !== teamId);
      } else {
        return [...prev, teamId];
      }
    });
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // First, delete all existing assignments
      const { error: deleteError } = await supabase
        .from('client_teams')
        .delete()
        .eq('client_id', clientId);

      if (deleteError) throw deleteError;

      // Then, insert new assignments if there are any
      if (selectedTeams.length > 0) {
        const assignmentsToInsert = selectedTeams.map(teamId => ({
          client_id: clientId,
          team_id: teamId
        }));

        const { error: insertError } = await supabase
          .from('client_teams')
          .insert(assignmentsToInsert);

        if (insertError) throw insertError;
      }

      toast({
        title: 'Success',
        description: 'Teams updated successfully'
      });

      if (onComplete) {
        onComplete();
      }
    } catch (error: any) {
      console.error('Error saving team assignments:', error);
      toast({
        title: 'Error',
        description: 'Failed to save team assignments',
        variant: 'destructive'
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Function to get assigned teams for display (not exported, just used internally)
  const getAssignedTeams = () => {
    return teams.filter(team => selectedTeams.includes(team.id));
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        {Array.from({ length: 3 }).map((_, index) => (
          <Card key={index} className="p-4 animate-pulse">
            <div className="h-6 bg-gray-200 rounded w-1/2" />
          </Card>
        ))}
      </div>
    );
  }

  // For display-only mode (just show assigned teams)
  if (displayOnly) {
    const assignedTeams = teams.filter(team => selectedTeams.includes(team.id));
    
    if (assignedTeams.length === 0) {
      return (
        <div className="text-sm text-gray-500">
          No teams assigned.
        </div>
      );
    }
    
    return (
      <div className="space-y-2">
        {assignedTeams.map(team => (
          <div key={team.id} className="text-sm">
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
              {team.name}
            </span>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="space-y-2 max-h-[400px] overflow-y-auto">
        {teams.length === 0 ? (
          <div className="text-center text-gray-500 py-6">
            No teams available
          </div>
        ) : (
          teams.map((team) => (
            <Card key={team.id} className="p-4">
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id={`team-${team.id}`}
                  checked={selectedTeams.includes(team.id)}
                  onCheckedChange={() => handleTeamToggle(team.id)}
                />
                <label
                  htmlFor={`team-${team.id}`}
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer flex-1"
                >
                  <div className="font-medium">{team.name}</div>
                  {team.description && (
                    <div className="text-xs text-gray-500 mt-1">{team.description}</div>
                  )}
                </label>
              </div>
            </Card>
          ))
        )}
      </div>

      <div className="flex justify-end space-x-2 pt-4">
        <Button 
          variant="outline" 
          onClick={onComplete}
          disabled={isSaving}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleSave}
          disabled={isSaving}
        >
          {isSaving ? 'Saving...' : 'Save'}
        </Button>
      </div>
    </div>
  );
};
